import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useQueryClient } from 'react-query';
import { auth } from '../config/firebase';
import { AuthContext } from '../auth/AuthContext';
import { useAuth } from '../auth/useAuth';
import { getCachedSessionPolicyHours } from '../auth/sessionPolicy';
import { OFFLINE_ENABLED } from '../offline/entities';
import { isEffectivelyOnline, subscribeConnectivity } from '../offline/connectivity';

/**
 * ADR-0017: in-place re-auth for the absolute session lifetime. Two modes off
 * AuthContext.sessionExpiry:
 *  - 'warn'   — deadline near; optional heads-up ("Not now" dismisses).
 *  - 'locked' — deadline passed or the API said SESSION_EXPIRED; blocking until
 *               re-auth succeeds (or the vet explicitly logs out).
 * A successful reauthenticateWithCredential resets Firebase auth_time, so the
 * session continues exactly where it was: no signOut, no replica wipe, no
 * redirect — unsynced field work stays queued and pushes on the next drain.
 */
const SessionReauthDialog: React.FC = () => {
  const { user, sessionExpiry, onSessionReauthed, dismissSessionWarning } = useContext(AuthContext);
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const locked = sessionExpiry === 'locked';
  // INV-19/INV-49: on an offline-opted-in device the overlay must yield while
  // offline — a server-expired session may still WORK offline against the
  // replica (A5 grace), and re-auth needs the network anyway. The 'locked'
  // state stays sticky in AuthProvider; we merely re-engage on reconnect.
  // Without the offline opt-in there is no replica to yield to, so the dialog
  // stays up (it explains why everything is failing).
  const [online, setOnline] = useState(() => isEffectivelyOnline());
  useEffect(() => {
    const update = () => setOnline(isEffectivelyOnline());
    const unsubscribe = subscribeConnectivity(update);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      unsubscribe();
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  const open = sessionExpiry !== 'none' && !!user && (online || !OFFLINE_ENABLED);
  const email = auth.currentUser?.email || user?.email || '';
  const hours = getCachedSessionPolicyHours();

  // Fresh form whenever the dialog (re)engages or escalates warn -> locked.
  useEffect(() => {
    setPassword('');
    setError('');
  }, [sessionExpiry]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!password) return;
    const current = auth.currentUser;
    if (!current || !email) {
      // Narrow race: a Firebase-side revocation is ending the session (the
      // dialog closes moments later) — explain rather than dead-button.
      setError('Your sign-in is no longer available here. Please log out and sign back in.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await reauthenticateWithCredential(current, EmailAuthProvider.credential(email, password));
      // Mint a token carrying the fresh auth_time so the very next request passes.
      await current.getIdToken(true);
      queryClient.invalidateQueries();
      setPassword('');
      onSessionReauthed();
    } catch (err: any) {
      const code: string = err?.code || '';
      if (
        code.includes('wrong-password') ||
        code.includes('invalid-credential') ||
        code.includes('invalid-login-credentials')
      ) {
        setError('Incorrect password. Please try again.');
      } else if (code.includes('network-request-failed')) {
        setError(
          "Can't reach the server. You seem to be offline. Your work is safe, so try again once you're back online.",
        );
      } else if (code.includes('too-many-requests')) {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        setError('Could not confirm your password. Try again, or log out and sign back in.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      // Warn is dismissible; locked only resolves through re-auth or logout.
      onClose={locked ? undefined : dismissSessionWarning}
      disableEscapeKeyDown={locked}
      maxWidth="xs"
      fullWidth
      aria-labelledby="session-reauth-title"
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle id="session-reauth-title">
          {locked ? 'Please sign in again' : 'Your session expires soon'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {locked
              ? 'For security, your session has reached its time limit. Enter your password to pick up exactly where you left off. Nothing is lost, including changes that have not synced yet.'
              : `For security, StableTrack sessions last up to ${hours ?? 24} hours. Re-enter your password now to keep working without interruption.`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {email}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            autoComplete="current-password"
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {locked ? (
            <Button onClick={() => logout()} color="inherit" disabled={submitting}>
              Log out
            </Button>
          ) : (
            <Button onClick={dismissSessionWarning} color="inherit" disabled={submitting}>
              Not now
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={submitting || !password}>
            {locked ? 'Sign in' : 'Stay signed in'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SessionReauthDialog;
